import { isEmpty, memoize } from "lodash-es";
import React from "react";
import { withRouter } from "react-router-dom";
import { IRoute } from "router";

import { ConsultantProfileIdObserver } from "@/components/User/ConsultantProfileIdObserver";
import { ICompany } from "@/data/companies";
import { getConsultantProfileCompany, IConsultantProfile, isProfileConnected } from "@/data/consultant-profiles";
import { isActive } from "@/data/consultant-profiles/utils/isActive";
import { ISessionUser } from "@/data/session";
import { TUserRole } from "@/data/session";
import { updateLastUsedConsultantProfile } from "@/data/users";
import { IWithRouteMatchProps } from "@/infrastructure/Route";
import { IWithResolvedSessionProps, withResolvedSession } from "@/infrastructure/Session";

import { HotJarIdentifier } from "../hot-jar/HotJarIdentifier";

interface IUserContextData {
  contextConsultantProfileId?: IConsultantProfile["id"];
}

export interface IUserContextState extends IUserContextData {
  setConsultantProfileContext: (consultantProfileId?: number) => Promise<void>;
  updateContextUser: (user: ISessionUser) => void;
  updateContextCompany: (company: ICompany) => void;
  updateContextUserProfile: (profile: IConsultantProfile) => void;

  getFirstAvailableProfile: (exceptId?: number) => IConsultantProfile | undefined;

  hasUserRoleInCompany: (role: TUserRole, companyId: ICompany["id"]) => boolean;
  hasUserRoleInContextCompany: (...roles: TUserRole[]) => boolean;
  hasRouteAccess: (route: IRoute) => boolean;
  hasConsultantProfile: (consultantProfileId: number) => boolean;

  hasCompanyAdminPrivileges: () => boolean;
  hasCompanyManagerPrivileges: () => boolean;

  canCreateProfile: () => boolean;
}

export interface IUserContext extends IUserContextState {
  contextConsultantProfile?: IConsultantProfile;
  contextCompany?: ICompany;
  sessionUser: ISessionUser;
}

export const UserContext = React.createContext({} as IUserContext);

interface Props extends IWithResolvedSessionProps<ISessionUser>, IWithRouteMatchProps {}
interface State extends IUserContextState {}

// TODO: it definitely needs to be rewritten
class UserContextProviderComponent extends React.Component<Props, State> {
  get sessionPayload() {
    return this.props.session.payload;
  }

  constructor(props: Props) {
    super(props);

    const contextConsultantProfileId = this.getInitialConsultantProfileId();

    this.state = {
      contextConsultantProfileId,

      setConsultantProfileContext: this.setConsultantProfileContext,
      updateContextUser: this.updateContextUser,
      updateContextCompany: this.updateContextCompany,
      updateContextUserProfile: this.updateContextUserProfile,

      getFirstAvailableProfile: this.getFirstAvailableProfile,

      hasUserRoleInCompany: this.hasUserRoleInCompany,
      hasUserRoleInContextCompany: this.hasUserRoleInContextCompany,
      hasRouteAccess: this.hasRouteAccess,
      hasConsultantProfile: this.hasConsultantProfile,

      hasCompanyAdminPrivileges: this.hasCompanyAdminPrivileges,
      hasCompanyManagerPrivileges: this.hasCompanyManagerPrivileges,

      canCreateProfile: this.canCreateProfile,
    };
  }

  // NOTE: VAT validation is fired only when logging in / refreshing token so we need to cache it within session
  cacheVatValidation = (user: ISessionUser): ISessionUser => {
    return {
      ...user,
      consultantProfiles: user.consultantProfiles.map(cp => ({
        ...cp,
        sharedProfile: cp.sharedProfile
          ? {
              ...cp.sharedProfile,
              company: {
                ...cp.sharedProfile.company,
                validVatNumber:
                  this.sessionPayload.consultantProfiles.find(p => p.id === cp.id)?.sharedProfile?.company
                    .validVatNumber ?? cp.sharedProfile.company.validVatNumber,
                validOrgNumber:
                  this.sessionPayload.consultantProfiles.find(p => p.id === cp.id)?.sharedProfile?.company
                    .validOrgNumber ?? cp.sharedProfile.company.validOrgNumber,
              },
            }
          : null,
      })),
    };
  };

  getInitialConsultantProfileId = () => {
    const { lastUsedConsultantProfileId } = this.sessionPayload;

    const lastUsedProfile = lastUsedConsultantProfileId
      ? this.getConsultantProfile(lastUsedConsultantProfileId)
      : undefined;

    return lastUsedProfile && isActive(lastUsedProfile) ? lastUsedProfile.id : undefined;
  };

  getConsultantProfile = (consultantProfileId: IConsultantProfile["id"]) => {
    const profile = this.sessionPayload.consultantProfiles.find(
      (p: IConsultantProfile) => p.id === consultantProfileId,
    );

    return profile || this.sessionPayload.consultantProfiles[0];
  };

  getFirstAvailableProfile = (exceptId?: number) => {
    let availableProfiles = this.sessionPayload.consultantProfiles;

    if (exceptId) {
      availableProfiles = availableProfiles.filter(p => p.id !== exceptId);
    }

    if (availableProfiles.length > 0) {
      return availableProfiles[0];
    }
    return undefined;
  };

  changeUserContext = async ({ contextConsultantProfileId }: IUserContextData) => {
    if (!(this.state.contextConsultantProfileId === contextConsultantProfileId)) {
      this.setState({ contextConsultantProfileId });

      await updateLastUsedConsultantProfile(contextConsultantProfileId);
    }
  };

  setConsultantProfileContext = async (contextConsultantProfileId: IConsultantProfile["id"]) => {
    await this.changeUserContext({ contextConsultantProfileId });
  };

  updateContextUser = (sessionUser: ISessionUser) => {
    const user = this.cacheVatValidation(sessionUser);
    this.props.session.update(user);
  };

  updateContextCompany = (company: ICompany) => {
    const payload = {
      ...this.sessionPayload,
      consultantProfiles: this.sessionPayload.consultantProfiles.map(c => {
        if (c.id === this.state.contextConsultantProfileId) {
          return { ...c, sharedProfile: { ...c.sharedProfile, company } } as IConsultantProfile;
        }

        return c;
      }),
    };
    this.updateContextUser(payload);
  };

  updateContextUserProfile = (profile: IConsultantProfile) => {
    const payload = {
      ...this.sessionPayload,
      consultantProfiles: this.sessionPayload.consultantProfiles.map(c => (c.id === profile.id ? profile : c)),
    };
    this.updateContextUser(payload);
  };

  roleInCompanybody = (role: TUserRole, companyId: ICompany["id"]) =>
    this.sessionPayload.roles.some(userRole => userRole.role === role && userRole.companyId === companyId);

  roleInCompanyTime = (role: TUserRole, companyId: ICompany["id"]) =>
    `${this.sessionPayload.modifiedDate}-${role}-${companyId}`;

  hasUserRoleInCompany = memoize(this.roleInCompanybody, this.roleInCompanyTime);

  hasUserRoleInContextCompany = (...roles: TUserRole[]) => {
    if (!this.state.contextConsultantProfileId) {
      return false;
    }

    const consultantProfile = this.getConsultantProfile(this.state.contextConsultantProfileId);
    const company = consultantProfile ? getConsultantProfileCompany(consultantProfile) : undefined;

    if (!company) {
      return false;
    }

    return roles.some(r => this.hasUserRoleInCompany(r, company.id));
  };

  hasRouteAccess = (route: IRoute) => {
    const profile = this.state.contextConsultantProfileId
      ? this.getConsultantProfile(this.state.contextConsultantProfileId)
      : undefined;

    return (
      isEmpty(route.acl) ||
      (route.acl.includes("PRIVATE") && profile && !isProfileConnected(profile)) ||
      route.acl.some((role: TUserRole) => this.hasUserRoleInContextCompany(role))
    );
  };

  hasConsultantProfile = (consultantProfileId: number) => {
    return this.sessionPayload.consultantProfiles.map(c => c.id).includes(consultantProfileId);
  };

  hasCompanyAdminPrivileges = () => {
    return this.hasUserRoleInContextCompany("ADMIN");
  };

  hasCompanyManagerPrivileges = () => {
    return this.hasUserRoleInContextCompany("ADMIN", "MANAGER");
  };

  canCreateProfile = () => {
    const profiles = this.sessionPayload.consultantProfiles;
    return profiles.every(isProfileConnected);
  };

  render() {
    const { children } = this.props;

    const consultantProfile = this.state.contextConsultantProfileId
      ? this.getConsultantProfile(this.state.contextConsultantProfileId)
      : undefined;

    return (
      <UserContext.Provider
        value={{
          ...this.state,
          sessionUser: this.sessionPayload,
          contextConsultantProfile: consultantProfile,
          contextCompany: consultantProfile && getConsultantProfileCompany(consultantProfile),
        }}>
        <ConsultantProfileIdObserver />
        <HotJarIdentifier />

        {children}
      </UserContext.Provider>
    );
  }
}

export const UserContextProvider = withResolvedSession(withRouter(UserContextProviderComponent));
export const UserContextConsumer = UserContext.Consumer;
